import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { ReactNode } from 'react'

import {
  getOtoIndividualPropositionPricingById,
  OtoIndividualPropositionPricing
} from '../../../api/enrollments/get-oto-individual-proposition-pricing'
import PriceLabel from '../../elements/price-label'

type Props = {
  pricing?: OtoIndividualPropositionPricing
  isPending: boolean
  isError: boolean
  title?: ReactNode
  className?: string
  // used to label the option line while the pricing is still loading, or when the backend
  // priced no option at all
  fallbackOptionTitle?: string
  // only the wizard lets the two discounts be toggled; a saved enrollment is read only
  includeApplicationFees?: boolean
  includeFamilyDiscount?: boolean
  onToggleApplicationFees?: () => void
  onToggleFamilyDiscount?: () => void
  // the "code avantage" box the wizard renders under the total
  children?: ReactNode
}

/**
 * The "Détail du tarif" of a One to One Individuel selection. Every amount comes from the backend
 * pricing engine — there is no price math here.
 */
const OtoIndividualPriceDetails = ({
  pricing,
  isPending,
  isError,
  title,
  className,
  fallbackOptionTitle,
  includeApplicationFees,
  includeFamilyDiscount,
  onToggleApplicationFees,
  onToggleFamilyDiscount,
  children
}: Props) => {
  const optionPrice = pricing?.options[0]?.price ?? null
  const optionTitle = pricing?.options[0]?.title ?? fallbackOptionTitle ?? 'Option'
  const transferPrice = pricing && pricing.mainTrip > 0 ? pricing.mainTrip : null
  const promoCodeDiscount = pricing?.promoCode?.discount ?? null

  return (
    <InnerBox
      className={cn('drop-shadow-lg bg-white rounded-lg p-5', className)}
      title={!title ? 'Détail du tarif (tout inclus)' : undefined}
      titleColor={tailwindColors.black.DEFAULT}
    >
      {title}

      {isPending ? (
        <Loader center />
      ) : isError || !pricing ? (
        <Typography variant="tag" color={tailwindColors.secondaryRed.DEFAULT}>
          Le tarif n’a pas pu être calculé. Veuillez réessayer.
        </Typography>
      ) : (
        <div className="flex flex-col justify-between h-full">
          <div className="flex flex-col gap-3">
            <PriceLabel
              pricesInfo={{
                label: 'Frais de séjour',
                price: pricing.baseJourneyPrice,
                isBadge: false
              }}
            />

            <PriceLabel
              title="Option(s) : "
              emptyMessage="Aucune option sélectionnée"
              pricesInfo={
                optionPrice != null
                  ? [{ label: optionTitle, price: optionPrice, isBadge: true }]
                  : []
              }
            />

            <PriceLabel
              title="Accueil-transfert : "
              emptyMessage="Arrivée directe dans la famille"
              pricesInfo={
                transferPrice != null
                  ? [{ label: 'Accueil-transfert', price: transferPrice, isBadge: true }]
                  : []
              }
            />

            {pricing.insurances.length > 0 && (
              <PriceLabel
                title="Assurance(s) : "
                pricesInfo={pricing.insurances.map((insurance) => ({
                  label: insurance.name,
                  price: insurance.price,
                  isBadge: true
                }))}
              />
            )}

            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center">
                {onToggleApplicationFees && (
                  <Checkbox
                    checked={!!includeApplicationFees}
                    onChange={onToggleApplicationFees}
                  />
                )}
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Frais de dossier
                </Typography>
              </div>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {pricing.applicationFee}€
              </Typography>
            </div>

            {promoCodeDiscount != null && (
              <PriceLabel
                pricesInfo={{
                  label: 'Code avantage',
                  price: promoCodeDiscount,
                  isBadge: false,
                  sign: '-'
                }}
              />
            )}
          </div>

          <div className="flex flex-col gap-3 mt-6">
            <div className={'h-1 w-full border-t border-dashed border-grey-light-hover'} />

            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center">
                {onToggleFamilyDiscount && (
                  <Checkbox checked={!!includeFamilyDiscount} onChange={onToggleFamilyDiscount} />
                )}
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Réduction famille
                </Typography>
              </div>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {pricing.familyDiscount}€
              </Typography>
            </div>

            <div className="flex items-center justify-between">
              <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
                Prix total
              </Typography>
              <Badge variant={'warning'} text={pricing.total + '€'} />
            </div>

            {children}
          </div>
        </div>
      )}
    </InnerBox>
  )
}

type EnrollmentProps = Pick<Props, 'title' | 'className' | 'fallbackOptionTitle'> & {
  enrollmentId: number
}

/**
 * The same breakdown for a saved One to One Individuel enrollment: the backend recomputes it from
 * the stored proposition, so nothing but the enrollment id is needed.
 */
export const OtoIndividualEnrollmentPriceDetails = ({
  enrollmentId,
  ...rest
}: EnrollmentProps) => {
  const { data, isPending, isError } = useQuery({
    queryKey: ['oto-individual-proposition-pricing-by-id', enrollmentId],
    queryFn: () => getOtoIndividualPropositionPricingById(enrollmentId)
  })

  return (
    <OtoIndividualPriceDetails {...rest} pricing={data} isPending={isPending} isError={isError} />
  )
}

export default OtoIndividualPriceDetails
